import React, { useState, useEffect } from 'react';
import { Check, Loader, ArrowRight, Crown, Users, Zap, Star } from 'lucide-react';
import api from '../services/api';
import { useSEO } from '../utils/seo';

const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useSEO({
    title: 'Pricing — Unlimited Users from $29/mo | TicketDesk',
    description: 'Simple, flat pricing with unlimited team members. Free plan available. No per-seat charges. Compare plans: Free, Starter ($29), Growth ($79), Scale ($149).',
    path: '/pricing',
  });

  useEffect(() => {
    fetchUserData();
    fetchPlans();
    fetchTrialStatus();

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) setPromoCode(code);
  }, []);

  const fetchUserData = async () => {
    try {
      const user = await api.getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const plansData = await api.getPlans();
      setPlans(plansData);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialStatus = async () => {
    try {
      const status = await api.getTrialStatus();
      setTrialStatus(status);
    } catch (error) {
      console.error('Failed to fetch trial status:', error);
    }
  };

  const getTrueBenefits = (plan) => {
const benefitsMap = {
  free: [
    '10 tickets per month',
    '10 reviews per month',
    'Unlimited team members',
      '1 workspace',
        'Full Integration Package'
  ],
  starter: [
    '200 tickets monthly',
    'Unlimited review scanning',
    'Unlimited team members',
    '1 workspace',
    'Full Integration Package',
    'AI powered responses'
  ],
  growth: [
    '1,000 tickets monthly',
    'Unlimited review scanning',
    'Unlimited team members',
    '5 workspaces',
    'Full Integration Package',
    'AI powered responses'
  ],
  scale: [
    '5,000 tickets monthly',
    'Unlimited review scanning',
    'Unlimited team members',
    'Unlimited workspaces',
    'Full Integration Package',
    'AI powered responses'
  ]
};
    
    return benefitsMap[plan.id] || plan.features;
  };

  const getPlanPrice = (plan) => {
    if (plan.price === null || plan.price === 0) return { monthly: 0, yearly: 0 };
    
    const monthlyPrice = plan.price;
    const yearlyPrice = Math.round(monthlyPrice * 12 * 0.8);
    
    return {
      monthly: monthlyPrice,
      yearly: yearlyPrice,
      yearlyMonthly: Math.round(yearlyPrice / 12)
    };
  };

  const getPlanIcon = (planId) => {
    const icons = {
      free: <Star className="h-6 w-6 text-gray-500" />,
      starter: <Users className="h-6 w-6 text-blue-500" />,
      growth: <Zap className="h-6 w-6 text-purple-500" />,
      scale: <Crown className="h-6 w-6 text-yellow-500" />
    };
    return icons[planId] || <Star className="h-6 w-6 text-gray-500" />;
  };

  const getCurrentPlanId = () => {
    if (!trialStatus) return null;
    
    if (trialStatus.hasActiveSubscription) {
      return trialStatus.currentPlan?.toLowerCase();
    } else if (trialStatus.isInTrial) {
      return null;
    } else if (trialStatus.isExpired) {
      return 'free';
    }
    return 'free';
  };

  const getButtonText = (plan) => {
    const currentPlanId = getCurrentPlanId();
    
    if (trialStatus?.isInTrial) {
      if (plan.id === 'free') {
        return 'Available after trial';
      }
      if (plan.id === 'starter') {
        return `Lock in Starter Plan (Keep ${trialStatus.daysLeft} trial days)`;
      }
      return `Lock in ${plan.name} Plan (Keep ${trialStatus.daysLeft} trial days)`;
    }
    
    if (plan.id === currentPlanId) {
      return 'Current Plan';
    }
    
    if (plan.id === 'free') {
      if (currentPlanId === 'free') {
        return 'Current Plan';
      }
      if (trialStatus?.hasActiveSubscription) {
        return 'Downgrade to Free';
      }
      return 'Start Free Trial';
    }
    
    const currentPlanIndex = plans.findIndex(p => p.id === currentPlanId);
    const thisPlanIndex = plans.findIndex(p => p.id === plan.id);
    
    if (currentPlanIndex > thisPlanIndex) {
      return `Downgrade to ${plan.name}`;
    } else {
      return 'Start Free Trial';
    }
  };

  const isButtonDisabled = (plan) => {
    const currentPlanId = getCurrentPlanId();
    
    if (plan.id === currentPlanId) {
      return true;
    }
    
    if (plan.id === 'free' && trialStatus?.isInTrial) {
      return true;
    }
    
    return false;
  };

  const handleSelectPlan = async (plan) => {
    if (isButtonDisabled(plan)) return;
    
    if (plan.id === 'free') {
      if (window.confirm('Are you sure you want to downgrade to the free plan? You will lose access to premium features.')) {
        setIsProcessing(true);
        try {
          await api.downgradeToFree();
          window.location.href = '/';
        } catch (error) {
          console.error('Failed to downgrade:', error);
        }
        setIsProcessing(false);
      }
      return;
    }

    if (plan.custom) {
      window.location.href = '/contact?plan=enterprise';
      return;
    }

    setIsProcessing(true);
    try {
      const planName = plan.id.toUpperCase();
      const interval = billingCycle === 'yearly' ? 'year' : 'month';
      
      console.log('Sending checkout data:', { plan: planName, interval: interval });
      
      const response = await api.createCheckoutSession({
        plan: planName,
        interval: interval
      });
      
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
    }
    setIsProcessing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const currentPlanId = getCurrentPlanId();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Simple Pricing, Unlimited Users
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            No per-seat fees. Add your whole team. Scale as you grow.
          </p>
          
          <div className="mt-8 flex items-center justify-center">
            <div className="bg-white rounded-lg p-1 shadow-md border border-gray-200">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  billingCycle === 'yearly'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly (Save up to $358)
              </button>
            </div>
          </div>

          {trialStatus && trialStatus.isInTrial && (
            <div className="mt-6">
              <div className="inline-flex items-center px-4 py-3 bg-green-100 text-green-800 rounded-full">
                <Check className="h-5 w-5 mr-2" />
                {trialStatus.daysLeft} days left in your trial
              </div>
              <p className="mt-3 text-sm text-gray-600">
                💡 <strong>Lock in your plan now:</strong> Choose a plan and you'll only be charged after your {trialStatus.daysLeft} trial days are complete.
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {plans.filter(plan => plan.id !== 'enterprise').map((plan) => {
            const pricing = getPlanPrice(plan);
            const isPopular = plan.id === 'starter';
            const isForAgencies = plan.id === 'scale';
            const isCurrentPlan = plan.id === currentPlanId;
            const isOnTrialForThisPlan = trialStatus?.isInTrial;
            
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl bg-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col ${
                  isPopular && !isOnTrialForThisPlan ? 'ring-2 ring-purple-500' : ''
                } ${isCurrentPlan ? 'ring-2 ring-green-500' : ''} ${isOnTrialForThisPlan && plan.id === 'starter' ? 'ring-2 ring-blue-500' : ''}`}
              >
                {isPopular && !isOnTrialForThisPlan && (
                  <div className="absolute -top-5 left-0 right-0 mx-auto w-32">
                    <div className="rounded-full bg-purple-600 px-3 py-2 text-center text-sm font-bold text-white shadow-lg">
                      Most Popular
                    </div>
                  </div>
                )}
                
                {isForAgencies && !isOnTrialForThisPlan && (
                  <div className="absolute -top-5 left-0 right-0 mx-auto w-32">
                    <div className="rounded-full bg-purple-600 px-3 py-2 text-center text-sm font-bold text-white shadow-lg">
                      For Agencies
                    </div>
                  </div>
                )}

                {isOnTrialForThisPlan && plan.id === 'starter' && (
                  <div className="absolute -top-5 left-0 right-0 mx-auto w-32">
                    <div className="rounded-full bg-blue-500 px-3 py-2 text-center text-sm font-bold text-white shadow-lg">
                      On Trial
                    </div>
                  </div>
                )}
                
                <div className="p-6 flex flex-col flex-grow">
                  <div>
                  <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">
                    {plan.name}
                  </h3>
                  
                  <div className="text-center">
                    {pricing.monthly !== 0 ? (
                      <>
                        <div className="flex items-baseline justify-center">
                          <span className="text-5xl font-bold text-purple-600">
                            ${billingCycle === 'yearly' ? pricing.yearlyMonthly : pricing.monthly}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">per month</p>
                      </>
                    ) : (
                      <>
                        <div className="text-5xl font-bold text-gray-900">$0</div>
                        <p className="text-sm text-gray-600 mt-1">forever</p>
                      </>
                    )}
                  </div>
                  
                  <div className="mt-4 text-center">
                    {plan.id === 'free' && (
                      <>
                        <p className="text-sm text-gray-700 font-medium">10 tickets/month</p>
                        <p className="text-sm text-purple-600 font-medium mt-1">7-day trial with full access</p>
                      </>
                    )}
                    {plan.id === 'starter' && (
                      <p className="text-lg text-purple-600 font-bold">200 tickets/month</p>
                    )}
                    {plan.id === 'growth' && (
                      <p className="text-lg text-gray-700 font-medium">1,000 tickets/month</p>
                    )}
                    {plan.id === 'scale' && (
                      <>
                        <p className="text-lg text-purple-600 font-bold">5,000 tickets/month</p>
                        <p className="text-sm text-purple-600 font-bold mt-1">UNLIMITED WORKSPACES</p>
                      </>
                    )}
                  </div>

                  <div className="mt-6">
                    <ul className="space-y-3">
                      {getTrueBenefits(plan).map((benefit, i) => (
                        <li key={i} className="flex items-start">
                          <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

<br/>
                  </div>

                  <button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={isProcessing || isButtonDisabled(plan)}
                    className={`mt-auto w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      isButtonDisabled(plan)
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : isPopular || isForAgencies || isOnTrialForThisPlan
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-700 text-white hover:bg-gray-800'
                    } disabled:opacity-50`}
                  >
                    {isProcessing ? (
                      <Loader className="h-5 w-5 animate-spin mx-auto" />
                    ) : (
                      getButtonText(plan)
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 flex items-center justify-center gap-8 text-sm text-gray-600">
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-500 mr-2" />
            No setup fees
          </div>
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-500 mr-2" />
            Cancel anytime
          </div>
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-500 mr-2" />
            30-day money back guarantee
          </div>
        </div>

        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-medium text-gray-900">
                Do I need a credit card for the trial?
              </h3>
              <p className="mt-2 text-gray-600">
                No! Start your 7-day trial without any payment information. Add your card only when you're ready to continue.
              </p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900">
                What happens after my trial ends?
              </h3>
              <p className="mt-2 text-gray-600">
                You'll be switched to our Free plan (10 tickets/month). Your data remains safe and you can upgrade anytime.
              </p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900">
                Can I change plans later?
              </h3>
              <p className="mt-2 text-gray-600">
                Yes! Upgrade or downgrade anytime. Changes take effect immediately and are prorated.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-gray-900">
                How does the annual discount work?
              </h3>
              <p className="mt-2 text-gray-600">
                Pay yearly and save 20% compared to monthly billing. You'll be charged once per year and can still cancel or change plans anytime.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;